import API from 'services/API';

const PING = 'ping';
const RETRY_DELAY = 5000;
const DEFAULT_POLL_INTERVAL = 10000;

/**
 * Abonnement temps réel aux checklists.
 *
 * `EventSource` ne permet pas d'envoyer le header `Authorization` : on lit
 * donc le flux SSE (GET /checklist/events) via `fetch`, ce qui garde le JWT.
 * Si le flux ne peut pas être ouvert (proxy, réseau...), un repli polling
 * déclenche `onEvent({ type: 'poll' })` régulièrement pour que l'appelant
 * recharge la vue courante.
 *
 * @param {(event: {type: string, instanceId?: number}) => void} onEvent
 * @param {{ pollInterval?: number }} [options]
 * @returns {() => void} fonction de désabonnement
 */
export const subscribeChecklistEvents = (onEvent, options = {}) => {
  const pollInterval = options.pollInterval || DEFAULT_POLL_INTERVAL;
  const baseUrl = (API.defaults.baseURL || '').replace(/\/$/, '');
  let stopped = false;
  let controller = null;
  let pollTimer = null;
  let retryTimer = null;

  const startPolling = () => {
    if (pollTimer || stopped) return;
    pollTimer = setInterval(() => onEvent({ type: 'poll' }), pollInterval);
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const handleChunk = chunk => {
    const data = chunk
      .split('\n')
      .filter(line => line.startsWith('data:'))
      .map(line => line.replace(/^data:\s?/, ''))
      .join('\n');
    if (!data) return;
    try {
      const event = JSON.parse(data);
      if (event && event.type && event.type !== PING) {
        onEvent(event);
      }
    } catch (err) {
      // Charge utile non JSON : ignorée.
    }
  };

  const connect = async () => {
    if (stopped) return;
    controller = new AbortController();
    try {
      const response = await fetch(`${baseUrl}/checklist/events`, {
        headers: {
          Accept: 'text/event-stream',
          Authorization: API.defaults.headers.common.Authorization || '',
        },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`SSE unavailable (${response.status})`);
      }
      stopPolling();
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done || stopped) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        chunks.forEach(handleChunk);
      }
    } catch (err) {
      // Flux interrompu : on bascule en polling (démarré ci-dessous).
    }
    if (!stopped) {
      startPolling();
      retryTimer = setTimeout(connect, RETRY_DELAY);
    }
  };

  connect();

  return () => {
    stopped = true;
    stopPolling();
    if (retryTimer) {
      clearTimeout(retryTimer);
    }
    if (controller) {
      controller.abort();
    }
  };
};
